import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ROOT_ID } from "./model/doc";
import { useYDoc } from "./hooks/useYDoc";
import { useUndoManager } from "./hooks/useUndo";
import { MindMapCanvas, LOCAL_ORIGIN, type CanvasHandle } from "./components/MindMapCanvas";
import { PresenceBar, usePresence } from "./components/PresenceBar";
import { HistoryPanel } from "./components/HistoryPanel";
import { FileMenu } from "./components/FileMenu";
import { ViewMenu } from "./components/ViewMenu";
import { HelpPanel } from "./components/HelpPanel";
import { OutlinePanel } from "./components/OutlinePanel";
import { ToastHost } from "./components/ToastHost";
import { SearchBar, EMPTY_SEARCH, type SearchState } from "./components/SearchBar";
import { LanguageProvider, useLanguage } from "./i18n/useLanguage";
import { LANGUAGES } from "./i18n/translations";
import { generateRoomId } from "./roomId";
import { notify } from "./toast";
import "./App.css";

function getRoomIdFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  let room = params.get("room");
  if (!room) {
    room = generateRoomId();
    params.set("room", room);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }
  return room;
}

export default function App() {
  return (
    <LanguageProvider>
      <AppShell />
    </LanguageProvider>
  );
}

function AppShell() {
  const { t, language, setLanguage } = useLanguage();
  const roomId = useMemo(getRoomIdFromUrl, []);
  const { doc, provider, status, nodes, links, authorName, setAuthorName, lastSyncedAt } = useYDoc(roomId);
  const [selectedId, setSelectedId] = useState(ROOT_ID);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const nodeCount = Object.keys(nodes).length;
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState<SearchState>(EMPTY_SEARCH);
  const undoManager = useUndoManager(doc, LOCAL_ORIGIN);
  if (import.meta.env.DEV) {
    // помощ при диагностика в конзолата на браузъра (вж. hooks/useYDoc.ts за __mindmap)
    (window as unknown as Record<string, unknown>).__undoManager = undoManager;
  }
  const presence = usePresence(provider?.awareness, authorName, selectedId);
  const canvasRef = useRef<CanvasHandle | null>(null);

  // Заглавието на раздела следва избрания език И съдържанието на главната
  // клетка ("Заедност: <главна клетка>" / "Za-edno: <главна клетка>") - иначе
  // отворените в много табове карти изглеждат неразличими едни от други.
  const rootText = nodes[ROOT_ID]?.text;
  useEffect(() => {
    const mainCell = (rootText || "").replace(/\s+/g, " ").trim() || t.emptyNode;
    document.title = `${t.appName}: ${mainCell}`;
  }, [t, rootText]);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const jumpToNode = useCallback((nodeId: string) => {
    canvasRef.current?.focusNode(nodeId);
  }, []);

  function closeSearch() {
    setSearchOpen(false);
    setSearch(EMPTY_SEARCH);
  }

  function copyLink() {
    // Менюто се затваря веднага след избора, затова потвърждението е известие
    // (а не смяна на надписа на бутон, както беше, докато бутонът стоеше в лентата).
    if (!navigator.clipboard) {
      notify(t.copyLinkFailed);
      return;
    }
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => notify(t.copyLinkDone, "info"))
      .catch(() => notify(t.copyLinkFailed));
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1 className="app-title">
          {t.appName}
          <span className="app-tagline">{t.appTagline}</span>
        </h1>

        <div className="app-header-actions">
          <FileMenu
            doc={doc}
            onImported={() => setSelectedId(ROOT_ID)}
            onCopyLink={copyLink}
            onSearch={openSearch}
            onHistory={() => setHistoryOpen(true)}
          />
          <ViewMenu canvasRef={canvasRef} onOutline={() => setOutlineOpen(true)} />
          <button onClick={() => setHelpOpen(true)}>{t.help}</button>
          <a className="admin-link-btn" href="admin.html">
            {t.adminTitle}
          </a>
          <a className="admin-link-btn donate-link" href={t.donateUrl} target="_blank" rel="noopener noreferrer">
            {t.donate}
          </a>
        </div>

        <div className="app-header-right">
          {nodeCount > 100 && (
            <span className="node-count-warning" title={t.nodeCountWarning(nodeCount)}>
              ⚠ {nodeCount}
            </span>
          )}
          <select
            className="language-select"
            aria-label={t.language}
            value={language}
            onChange={(e) => setLanguage(e.target.value as typeof language)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <PresenceBar
            users={presence}
            status={status}
            authorName={authorName}
            onRename={setAuthorName}
            lastSyncedAt={lastSyncedAt}
          />
        </div>
      </header>

      {searchOpen && (
        <SearchBar
          nodes={nodes}
          state={search}
          onChange={setSearch}
          onClose={closeSearch}
          onJump={jumpToNode}
        />
      )}

      <main className="app-main">
        <MindMapCanvas
          doc={doc}
          nodes={nodes}
          links={links}
          selectedId={selectedId}
          onSelect={setSelectedId}
          presence={presence}
          undoManager={undoManager}
          search={search}
          onRequestSearch={openSearch}
          canvasRef={canvasRef}
        />
      </main>

      <footer className="app-footer">
        <span>{t.shortcutsHint}</span>
      </footer>

      {historyOpen && (
        <div className="modal-overlay" onClick={() => setHistoryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <HistoryPanel doc={doc} authorName={authorName} onClose={() => setHistoryOpen(false)} />
          </div>
        </div>
      )}

      {helpOpen && (
        <div className="modal-overlay" onClick={() => setHelpOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <HelpPanel onClose={() => setHelpOpen(false)} />
          </div>
        </div>
      )}

      {outlineOpen && (
        <div className="modal-overlay" onClick={() => setOutlineOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <OutlinePanel
              doc={doc}
              onClose={() => setOutlineOpen(false)}
              onJump={(nodeId) => {
                jumpToNode(nodeId);
                setOutlineOpen(false);
              }}
            />
          </div>
        </div>
      )}

      <ToastHost />
    </div>
  );
}
